import React from 'react';

import { useQuery } from '@apollo/client';
import { TableCell, TableRow } from '@opendoor/bricks/core';

import {
  LeadDetails,
  LeadDetailsVariables,
} from '../../graphql/athena/queries/__generated__/LeadDetails';
import { LwcDetailsForBrokerage_odProtosListingsServicesListwithcertaintyproduct_getLwcDetailsForBrokerage_lwcDetails as LwcDetailsForBrokerage } from '../../graphql/athena/queries/__generated__/LwcDetailsForBrokerage';
import { LEAD_DETAILS_QUERY } from '../../graphql/athena/queries/LeadDetails';
import { useGetExteriorAssessmentData } from '../../graphql/athena/useExteriorAssessment';
import {
  convertLeadDetails,
  getIsALALead,
  getOrvaCompletedAtDateString,
  getReadableAddressFromSingleLead,
  SingleLead,
} from '../../helpers/getAllLeadData';
import { getDisplayStateFromSingleLead } from './utils';

interface LwcDetailsTableRowProps {
  lwcDetails: LwcDetailsForBrokerage;
  agentCustomerUuid: string;
  handleClick: (arg: SingleLead) => void;
}

const LwcDetailsTableRow: React.FC<LwcDetailsTableRowProps> = ({
  lwcDetails: lwcLeadDetail,
  handleClick,
}) => {
  const { loading, error, data } = useQuery<LeadDetails, LeadDetailsVariables>(LEAD_DETAILS_QUERY, {
    context: {
      clientName: 'athena',
    },
    variables: { selldirectLeadToken: lwcLeadDetail.lwcProduct?.sellerLeadToken ?? '' },
  });

  const { data: exteriorAssessmentData, loading: exteriorAssessmentDataLoading } =
    useGetExteriorAssessmentData(
      data?.odProtosListingsServicesListwithcertaintyproduct.getSellDirectDetails?.lead
        ?.sellerInputUuid,
    );

  if (loading || exteriorAssessmentDataLoading) return null;

  if (error) return null;

  if (
    !data ||
    !lwcLeadDetail.lwcProduct ||
    !lwcLeadDetail.listing ||
    !lwcLeadDetail.agentLead ||
    !lwcLeadDetail.agentLead.id
  ) {
    return null;
  }

  const singleLead = convertLeadDetails(data, lwcLeadDetail, exteriorAssessmentData);

  const displayState = getDisplayStateFromSingleLead(singleLead);

  const sellDirectDetails =
    data?.odProtosListingsServicesListwithcertaintyproduct?.getSellDirectDetails;
  const lead = sellDirectDetails?.lead;

  const isALALead = getIsALALead(data);

  return (
    <TableRow
      borderTop="1px solid"
      borderColor="brand50"
      paddingTop={2}
      paddingBottom={2}
      _hover={{
        background: 'neutrals40',
        border: `1px solid var(--colors-neutrals30)`,
        cursor: 'pointer',
      }}
      onClick={() => handleClick(singleLead)}
    >
      <TableCell textAlign="left">{getReadableAddressFromSingleLead(singleLead)}</TableCell>
      <TableCell textAlign="left">
        {`${lead?.customerV2?.firstName} ${lead?.customerV2?.lastName}`}
      </TableCell>
      <TableCell textAlign="left">{displayState}</TableCell>
      <TableCell textAlign="left">
        {lwcLeadDetail.agentLead.assignedAgentCustomer?.fullName ?? '-'}
      </TableCell>
      <TableCell textAlign="left">{getOrvaCompletedAtDateString(data)}</TableCell>
      <TableCell textAlign="left">
        {singleLead.offerData?.state == 'WAITING_FOR_LIST_WITH_CERTAINTY'
          ? singleLead.offerData?.expiresAt
          : '-'}
      </TableCell>
      <TableCell textAlign="left" style={{ color: 'red' }}>
        {isALALead ? 'ALA' : '-'}
      </TableCell>
    </TableRow>
  );
};

export default LwcDetailsTableRow;
